import { FORMULARIO_VAZIO, type Formulario } from './formularioGarrafa.js';

/**
 * Rascunho do cadastro de garrafa, guardado na aba (sessionStorage).
 *
 * Por que existe: no celular a página pode recarregar no meio do cadastro — o
 * Android tira o navegador da memória ao abrir a câmera, e em desenvolvimento o
 * Vite recarrega a página quando a conexão dele cai. Sem rascunho, a pessoa
 * perdia foto, campos e posição, e refazia a leitura da IA (pagando de novo).
 *
 * Por que sessionStorage e não localStorage: sobrevive a recarregar e à aba
 * restaurada pelo Android, mas morre ao fechar a aba. Um cadastro esquecido não
 * reaparece dias depois em outro contexto. A chave leva o id da conta, para que
 * outra pessoa logando na mesma aba não herde o rascunho.
 */

/** Fotos já guardadas no servidor. `url` é a da frente, para a miniatura. */
export interface FotoSalva {
  photoKey: string;
  thumbKey: string;
  backPhotoKey: string | null;
  url: string;
}

export interface RascunhoGarrafa {
  versao: 1;
  salvoEm: number;
  form: Formulario;
  camposEditados: Array<keyof Formulario>;
  fotoSalva: FotoSalva | null;
  confianca: number | null;
  cellarId: number | null;
  vaga: { shelfId: number; position: number } | null;
  /** Fotos (ver `fotosDaLeitura`) cuja leitura da IA já foi aplicada ao formulário. */
  leituraConcluida: string | null;
  /**
   * Leitura disparada e ainda sem resposta. Se a página recarregar com isto
   * preenchido, a leitura é retomada — e o servidor devolve a que já estava em
   * andamento, sem nova cobrança.
   */
  leituraPendente: { fotos: string; desde: number } | null;
}

/** Depois disso um rascunho esquecido é descartado. */
const VALIDADE_MS = 24 * 60 * 60 * 1000;

/**
 * Até quando uma leitura interrompida é retomada sozinha. Fica abaixo da
 * memória de leituras do servidor (30 min): retomar dentro dela não custa nada;
 * depois, a pessoa decide pelo botão.
 */
export const RETOMAR_LEITURA_MS = 10 * 60 * 1000;

export function chaveDoRascunho(userId: number): string {
  return `minha-adega:rascunho-garrafa:u${userId}`;
}

/** Identifica o conjunto de fotos lido: trocar a frente ou somar o verso muda o valor. */
export function fotosDaLeitura(fotos: FotoSalva): string {
  return `${fotos.photoKey}|${fotos.backPhotoKey ?? ''}`;
}

function temConteudo(rascunho: Omit<RascunhoGarrafa, 'versao' | 'salvoEm'>): boolean {
  if (rascunho.fotoSalva) return true;
  return (Object.keys(FORMULARIO_VAZIO) as Array<keyof Formulario>).some(
    (campo) => rascunho.form[campo] !== FORMULARIO_VAZIO[campo],
  );
}

export function lerRascunho(chave: string, agora = Date.now()): RascunhoGarrafa | null {
  try {
    const texto = sessionStorage.getItem(chave);
    if (!texto) return null;

    const dado = JSON.parse(texto) as Partial<RascunhoGarrafa>;
    if (dado.versao !== 1 || typeof dado.salvoEm !== 'number' || agora - dado.salvoEm > VALIDADE_MS) {
      sessionStorage.removeItem(chave);
      return null;
    }

    const rascunho: RascunhoGarrafa = {
      versao: 1,
      salvoEm: dado.salvoEm,
      // Mescla com o vazio: um campo novo no formulário não quebra rascunho antigo.
      form: { ...FORMULARIO_VAZIO, ...dado.form },
      camposEditados: Array.isArray(dado.camposEditados) ? dado.camposEditados : [],
      fotoSalva: dado.fotoSalva ?? null,
      confianca: dado.confianca ?? null,
      cellarId: dado.cellarId ?? null,
      vaga: dado.vaga ?? null,
      leituraConcluida: dado.leituraConcluida ?? null,
      leituraPendente: dado.leituraPendente ?? null,
    };

    return temConteudo(rascunho) ? rascunho : null;
  } catch {
    // Armazenamento bloqueado ou conteúdo corrompido: segue sem rascunho.
    return null;
  }
}

/** Grava o estado atual; sem foto nem campo preenchido, apaga em vez de gravar. */
export function gravarRascunho(chave: string, estado: Omit<RascunhoGarrafa, 'versao' | 'salvoEm'>): void {
  try {
    if (!temConteudo(estado)) {
      sessionStorage.removeItem(chave);
      return;
    }
    const rascunho: RascunhoGarrafa = { versao: 1, salvoEm: Date.now(), ...estado };
    sessionStorage.setItem(chave, JSON.stringify(rascunho));
  } catch {
    // Sem armazenamento o cadastro funciona igual, só não resiste a recarregar.
  }
}

export function apagarRascunho(chave: string): void {
  try {
    sessionStorage.removeItem(chave);
  } catch {
    // idem
  }
}
